import React, { useEffect, useRef } from 'react';
import { useMutation, useQuery, useLazyQuery } from '@apollo/react-hooks';
import Trash from '@jda/lui-common-icon-library/ui/Trash';
import { JsonPathPicker } from 'react-json-to-html-picker';
import ArrowChevronLeft from '@jda/lui-common-icon-library/ui/ArrowChevronLeft';
import JsonEditor from '../../components/JsonEditor';
import {
  Grid,
  Typography,
  Button,
  Select,
  MenuItem,
  Box,
  InputLabel,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle
} from '@material-ui/core';
import { useHistory, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { DialogForm, FieldInfo } from '../../components/DialogForm';
import { CertificationConfic, keysToShow } from './CertificationConfic';
import Edit from '@jda/lui-common-icon-library/ui/Edit';
import { Form } from '../../components/Form';
import {
  GET_SIMULATION_TEST_DATA,
  WORKFLOW_LIST,
  USER_DETAILS,
  GET_SIMULATION_DATA,
  POST_SIMULATION_TEST_DATA
} from '../graphQL/queries';
import useStyles from './Style';
import { formatString } from '../../utils/helpers';

const AddCertification = (props: any) => {
  const { formType, operation, toggleModal, workflowList } = props;
  const history = useHistory();
  const initialState = {
    path: '',
    field: { displayName: '', path: '' },
    isDeleteOpen: false,
    workflowName: operation?.serviceNm || '',
    workflowCode: operation?.workflowCode || '',
    operationName: operation?.operation || '',
    operationCode: operation?.operation && operation?.serviceNm ? operation?.operation : '',
    jsonData: '',
    testDataName: operation?.testDataName || '',
    description: operation?.description || '',
    edit: false,
    open: false,
    editField: false,
    preview: false,
    configFields: '[]',
    previewFields: [],
    formData: {
      path: '',
      displayName: '',
      formType: '',
      required: false,
      hide: false,
      mapFromPath: '',
      external: false
    },
    simulationConfig: CertificationConfic
  };

  const [
    {
      path,
      operationCode,
      workflowCode,
      workflowName,
      operationName,
      jsonData,
      testDataName,
      description,
      edit,
      open,
      formData,
      isDeleteOpen,
      field,
      configFields,
      editField,
      preview,
      previewFields,
      simulationConfig
    },
    setFormField
  ] = React.useState({
    ...initialState
  });

  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [postSimulateTestdData] = useMutation(POST_SIMULATION_TEST_DATA);
  const { loading: userLoading, data: userData } = useQuery(USER_DETAILS, { fetchPolicy: 'cache-first' });
  const [getSimulationTestData, { data: testdata }] = useLazyQuery(GET_SIMULATION_TEST_DATA, {
    fetchPolicy: 'network-only'
  });
  const [getSimulationData, { data: simulationData }] = useLazyQuery(GET_SIMULATION_DATA, {
    fetchPolicy: 'network-only'
  });

  const jsonInputEditorRef = useRef<any>();
  const setInputEditorRef = (instance: any) => {
    if (instance) {
      jsonInputEditorRef.current = instance.jsonEditor;
    } else {
      jsonInputEditorRef.current = null;
    }
  };

  useEffect(() => {
    if (formType === 'edit') {
      getSimulationData({
        variables: {
          search: `serviceNm=${workflowName}&testDataName=${operation?.testDataName}`
        }
      });
    }
  }, [workflowName, operationCode, formType]);

  useEffect(() => {
    setDefaultValues();
  }, [simulationData, testdata]);

  const setDefaultValues = () => {
    if (formType === 'edit' && simulationData) {
      const testConfic = simulationData?.simulatedData[0]?.input;
      const description = simulationData?.simulatedData[0]?.description;
      const configFields = simulationData?.simulatedData[0]?.configSpec;
      setFormField(prevState => ({ ...prevState, jsonData: testConfic, description, configFields }));
    } else if (formType === 'add' && testdata) {
      setFormField(prevState => ({
        ...prevState,
        jsonData: testdata?.getSimulationTestData?.data,
        configFields: '[]'
      }));
    }
  };

  const handleFormFields = ({ target: { name, value } }: any) => {
    let workflowObj = [] as any;
    if (name === 'workflowName') {
      workflowObj = workflowList?.data?.workflow?.content?.filter((item: any) => item.workflowName === value);
      setFormField(prevState => ({ ...prevState, workflowCode: workflowObj[0]?.workflowCode }));
    }
    if (name === 'operationName') {
      const operationObj = workflowList?.data?.workflow?.content
        ?.filter((item: any) => item.workflowCode === workflowCode)[0]
        ?.operations?.filter((item: any) => item.operationName === value);
      setFormField(prevState => ({ ...prevState, operationCode: operationObj[0]?.operationCode }));
    }
    setFormField(prevState => ({ ...prevState, [name]: value }));
  };

  const toggleFieldDialog = () => {
    const data = { ...formData, path };
    setFormField(prevState => ({ ...prevState, open: !open, formData: data }));
  };

  const handleEditField = (field: any) => {
    toggleFieldDialog();
    if (field?.type === 'Enum') {
      field['values'] = field?.enumValues?.toString();
    }
    setFormField(prevState => ({ ...prevState, formData: { ...formData, ...field }, editField: !editField }));
  };

  const handleFieldSubmit = (data: any) => {
    if (data?.type === 'Enum') {
      data['enumValues'] = data?.values?.split(',');
    }
    delete data?.values;
    let configSpec = '';
    if (editField) {
      configSpec = JSON.stringify(
        formatString(configFields).map((item: any) => {
          if (item.path === data.path) return { ...item, ...data };
          else return item;
        })
      );
    } else {
      configSpec = JSON.stringify(formatString(configFields).concat(data));
    }
    setFormField(prevState => ({ ...prevState, configFields: configSpec, path: '', editField: false }));
    toggleFieldDialog();
  };

  const toggleJSONEdit = () => {
    setFormField(prevState => ({ ...prevState, edit: !edit }));
  };

  const updateTestConfig = () => {
    setFormField(prevState => ({ ...prevState, jsonData: jsonInputEditorRef.current?.getText(), edit: !edit }));
  };
  const fetchSimulation = () => {
    if (workflowCode && operationCode) {
      if (formType === 'edit') {
        getSimulationData({
          variables: {
            search: `serviceNm=${workflowCode}`
          }
        });
      } else if (formType === 'add') {
        getSimulationTestData({ variables: { workflowCode, operationCode } });
      }
    }
  };

  const deleteFeild = (field: any) => {
    toggleDeleteModal();
    setFormField(prevState => ({ ...prevState, field }));
  };

  const toggleDeleteModal = () => {
    setFormField(prevState => ({ ...prevState, isDeleteOpen: !isDeleteOpen }));
  };

  const handleDeleteField = () => {
    const fields = formatString(configFields);
    const result = fields?.filter((item: any) => item.path !== field.path);
    setFormField(prevState => ({ ...prevState, configFields: JSON.stringify(result) }));
    toggleDeleteModal();
  };

  const handleSimulationSubmit = () => {
    let method = 'POST';
    if (formType === 'edit') method = 'PUT';
    postSimulateTestdData({
      variables: {
        testDataName: testDataName,
        description: description,
        serviceNm: workflowName,
        operation: operationName,
        input: jsonData,
        configSpec: configFields,
        testScenarioSeq: [],
        method
      }
    })
      .then((response: any) => {
        enqueueSnackbar('Simulation Updated  Successfully !!', {
          variant: 'success',
          preventDuplicate: true
        });
        if (formType === 'add') {
          setFormField(prevState => ({ ...prevState, ...initialState }));
          toggleModal();
        } else {
          setFormField(prevState => ({
            ...prevState,
            edit: false,
            open: false,
            editField: false,
            preview: false,
            jsonData: jsonInputEditorRef.current?.getText() || jsonData
          }));
          toggleModal();
        }
      })
      .catch((error: any) => {
        enqueueSnackbar('Simulation Update failed !!', {
          variant: 'error',
          preventDuplicate: true
        });
      });
  };

  const togglePreview = () => {
    const fields = formatString(configFields);
    fields?.map((field: any) => {
      field.size = 3;
      field.label = field.displayName;
      field.defaultValue = field?.defaultValue ? field?.defaultValue : null;
      const keys = field.path.split('.');
      field.key = keys[keys.length - 1];
      if (field?.required) {
        field['rules'] = {
          required: {
            value: true,
            message: `${field.displayName} is Required`
          }
        };
      }
      if (field.type === 'String') field.type = 'text';
      else if (field.type === 'Integer') field.type = 'number';
      else if (field.type === 'Enum') {
        field.type = 'dropdown';
        field.size = 3;
        field.labelStyleType = 'InputLabel';
        field.values = field?.enumValues?.map((val: string) => ({ label: val, value: val }));
      } else if (field.type === 'Date') field.type = 'date';
    });
    setFormField(prevState => ({ ...prevState, preview: !preview, previewFields: fields }));
  };

  const getFormValues = (values: any) => {
    simulationConfig?.map((item: any) => {
      if (item.key === 'mapFromPath') {
        item.type = values?.hide ? 'text' : 'hidden';
      }
      if (item.key === 'values') {
        item.type = values?.type === 'Enum' ? 'text' : 'hidden';
      }
      return item;
    });
    setFormField(prevState => ({ ...prevState, simulationConfig }));
  };

  const handlePathChange = (path: string) => {
    const pathString = path
      .replace('[', '')
      .replace(']', '')
      .split(',');
    let pathText = '';
    pathString?.map((item: any) => {
      if (item === '0') {
        pathText = `${pathText}[${item}]`;
      } else if (pathText !== '') {
        pathText = `${pathText}.${item}`;
      } else {
        pathText = item;
      }
    });
    handleFormFields({ target: { name: 'path', value: pathText.replace(/["']/g, '') } });
  };

  return (
    <Box px={2} width="100%">
      <Grid container>
        <Grid item xs={12} className={classes.header}>
          <Grid item xs={3}>
            <InputLabel id="workflow-label">Workflow</InputLabel>
            <Select
              id="Select-workflow"
              data-id="Select-workflow"
              name="workflowName"
              onChange={handleFormFields}
              value={workflowName}
              className={classes.select}
              disabled={formType === 'edit'}
            >
              {workflowList?.data?.workflow?.content?.map((workflow: any, index: any) => (
                <MenuItem value={workflow.workflowName} key={index}>
                  {workflow.workflowName}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={3}>
            <InputLabel id="operationName-label">Operation</InputLabel>
            <Select
              id="Select-simulation"
              data-id="Select-simulation"
              name="operationName"
              onChange={handleFormFields}
              value={operationName}
              className={classes.select}
              disabled={formType === 'edit'}
            >
              {workflowList?.data?.workflow?.content
                ?.filter((workflow: any) => workflow.workflowCode === workflowCode)?.[0]
                ?.operations?.map((operation: any) => (
                  <MenuItem value={operation.operationName}>{operation.operationName}</MenuItem>
                ))}
            </Select>
          </Grid>
          <Grid item xs={3}>
            <InputLabel id="testData-label">Test Data Name</InputLabel>
            <TextField
              data-id="testDataName-input"
              id="testDataName"
              name="testDataName"
              value={testDataName}
              onChange={handleFormFields}
              style={{ width: '80%' }}
              disabled={formType === 'edit'}
              required
            ></TextField>
          </Grid>
          <Grid item xs={3}>
            <InputLabel id="description-label">Description</InputLabel>
            <TextField
              data-id="description-input"
              id="description"
              name="description"
              value={description}
              onChange={handleFormFields}
              style={{ width: '80%' }}
              required
            ></TextField>
          </Grid>
          <Grid item xs={3}>
            <Button
              data-id="fetch-btn"
              id="fetch-btn"
              onClick={fetchSimulation}
              variant="outlined"
              color="primary"
              style={{ float: 'right' }}
              disabled={!workflowCode || !operationCode || !testDataName}
            >
              Fetch Simulation
            </Button>
          </Grid>
        </Grid>
        {jsonData && (
          <Grid container className={classes.header}>
            <Grid item xs={12} direction="row-reverse" style={{ display: 'flex' }}>
              <Button data-id="preview-btn" id="preview-btn" onClick={togglePreview} variant="outlined" color="primary">
                Preview
              </Button>
              <Button data-id="submit-btn" onClick={handleSimulationSubmit} variant="outlined" color="primary">
                Submit
              </Button>
              <Button data-id="submit-btn" onClick={setDefaultValues} variant="outlined" color="primary">
                Reset
              </Button>
              <Button data-id="submit-btn" onClick={toggleModal} variant="outlined" color="primary">
                Cancel
              </Button>
            </Grid>
            <Grid item xs={12} style={{ display: 'flex' }}>
              <Grid item xs={6} style={{ borderRight: '1px solid #ececec', overflow: 'auto' }}>
                <Button
                  id="edit-btn"
                  onClick={edit ? updateTestConfig : toggleJSONEdit}
                  variant="outlined"
                  color="primary"
                  style={{ float: 'right' }}
                >
                  {edit ? 'Update JSON' : 'Click To Edit The JSON'}
                </Button>
                {edit ? (
                  <JsonEditor
                    data-id="transform-input-editor"
                    setRef={setInputEditorRef}
                    data={formatString(jsonData)}
                    mode="code"
                    view={false}
                    id={'JsonEditorInput'}
                    allowedModes={['code', 'tree']}
                    theme="ace/theme/github"
                    htmlElementProps={{
                      style: {
                        height: '60vh',
                        width: '30vw'
                      }
                    }}
                  />
                ) : (
                  <JsonPathPicker json={jsonData} onChoose={(path: any) => handlePathChange(path)} path={path} />
                )}
              </Grid>
              <Grid item xs={6} style={{ padding: '2%' }}>
                <Typography color="textPrimary" className={classes.label}>
                  Selected Path
                  <FieldInfo title="Please choose the path to add field by clicking the selecr icon beside the key in JSON" />
                  : <b>{path || ''}</b>
                </Typography>
                <Button
                  data-id="addSimulation-button"
                  onClick={toggleFieldDialog}
                  variant="contained"
                  color="primary"
                  disabled={path === ''}
                >
                  Click to Add field
                </Button>
                <Grid container>
                  {configFields &&
                    formatString(configFields)?.map((field: any) => {
                      return (
                        <Grid item xs={4} className={classes.fieldList}>
                          {Object.keys(field).map((key: string) => (
                            <>
                              {keysToShow.includes(key) && (
                                <p color="textPrimary" style={{ wordBreak: 'break-all' }}>
                                  {key}: <b>{field[key]?.toString()}</b>
                                </p>
                              )}
                            </>
                          ))}
                          <span>
                            <Edit onClick={() => handleEditField(field)} style={{ cursor: 'pointer' }} />
                            <Trash onClick={() => deleteFeild(field)} style={{ cursor: 'pointer' }} />
                          </span>
                        </Grid>
                      );
                    })}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
      <DialogForm
        openFn={open}
        closeFn={toggleFieldDialog}
        dialogId="json-field-dialog"
        title={`Add Simulation`}
        data={formData}
        dataConfig={simulationConfig}
        handleDialogClose={toggleFieldDialog}
        handleDialogSubmit={handleFieldSubmit}
        submitlabel="Submit"
        maxWidth="lg"
        getLatestValues={getFormValues}
      ></DialogForm>
      {isDeleteOpen && (
        <DialogForm
          openFn={isDeleteOpen}
          dataConfig={[]}
          handleDialogClose={toggleDeleteModal}
          handleDialogSubmit={handleDeleteField}
          submitlabel={'Delete'}
          title={`Delete Field`}
          dialogId="delete-field-id"
          closeFn={toggleDeleteModal}
          maxWidth="sm"
        >
          <Typography color="textPrimary">{`Are you sure want to delete ${field?.displayName} ?`}</Typography>
        </DialogForm>
      )}
      <Dialog
        maxWidth="lg"
        fullWidth
        open={preview}
        onClose={togglePreview}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        id="custom-grid-dialog"
      >
        <DialogTitle id="customgrid-dialog-title">
          <b>Preview</b>
        </DialogTitle>
        <DialogContent dividers>
          <Form className={classes.root} dataConfig={previewFields}>
            <Button variant="outlined" color="primary" onClick={togglePreview}>
              Ok
            </Button>
          </Form>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AddCertification;
